/* ============================================================
   ET - STICKY ATC — fixed bottom add-to-cart bar (product page)

   The bar owns no cart logic. It finds whichever add-to-cart the page already
   renders and delegates to it, so the shopper's live variant selection is what
   gets added:

     * Impact stock PDP (sections/main-product.liquid)
       Sets form="<product form id>" on the bar's button. Per the HTML form-owner
       rules that submits the main product form from outside it, and Impact's
       ProductForm._onSubmit (assets/theme.js:2892) intercepts it for the AJAX add.
       Variant state rides the theme's own `variant:change` event
       (assets/theme.js:3493, bubbles) — listened for on document so a
       product-rerender swap can't detach us.

     * ET redesign PDP (sections/et-pdp-product.liquid)
       Forwards the click to .et-pdp__cta-btn, which runs that section's own
       fetch to /cart/add.js (assets/et-pdp-product.js:385). Variant state is
       mirrored from the data-variant-id attribute that section keeps updated
       (assets/et-pdp-product.js:352).

   Visibility mirrors Impact's own ProductQuickAdd (assets/theme.js:3275):
   shown once the main ATC has scrolled past the top of the viewport, hidden
   again once the footer comes into view.
   ============================================================ */

(function () {
  'use strict';

  var ET_CTA = '.et-pdp__cta-btn';

  /* Impact's product form always contains <input name="id"> (snippets/buy-buttons.liquid:33),
     and HTMLFormElement is [LegacyOverrideBuiltIns], so `form.id` resolves to that INPUT,
     not to the id string — Impact itself relies on this (`this.id.value`, theme.js:2917).
     Always read the attribute, the way BuyButtons and ProductQuickAdd do. */
  function formIdOf(form) {
    if (!form) return '';
    var id = form.getAttribute('id') || '';
    if (!id || document.getElementById(id) !== form) return '';
    return id;
  }

  function findThemeForm() {
    var scoped = document.querySelector(
      '.shopify-section--main-product form[is="product-form"][action*="/cart/add"]'
    );
    if (scoped) return scoped;

    /* Unscoped fallback must still be a real ProductForm — never a payment-terms
       installment form or a product-card quick-buy form. */
    var candidates = document.querySelectorAll('form[is="product-form"][action*="/cart/add"]');
    for (var i = 0; i < candidates.length; i++) {
      var id = candidates[i].getAttribute('id') || '';
      if (id.indexOf('product-form-') === 0 && id.indexOf('installment') === -1) {
        return candidates[i];
      }
    }
    return null;
  }

  function initBar(root) {
    if (!root || root.dataset.etStickyInit === '1') return;
    root.dataset.etStickyInit = '1';

    var button = root.querySelector('[data-et-atc]');
    var priceEl = root.querySelector('[data-et-price]');
    var compareEl = root.querySelector('[data-et-compare]');
    var badgeEl = root.querySelector('[data-et-badge]');
    var thumbEl = root.querySelector('[data-et-thumb]');
    var paymentEl = root.querySelector('[data-et-payment]');

    var variants = {};
    var dataEl = root.querySelector('[data-et-sticky-variants]');
    if (dataEl) {
      try {
        variants = JSON.parse(dataEl.textContent) || {};
      } catch (e) {
        variants = {};
      }
    }

    function applyVariant(variantId) {
      var v = variants[String(variantId)];
      if (!v) return;

      if (priceEl) priceEl.textContent = v.price;

      if (compareEl) {
        compareEl.textContent = v.compare || '';
        if (v.compare) compareEl.removeAttribute('hidden');
        else compareEl.setAttribute('hidden', '');
      }

      if (badgeEl) {
        badgeEl.textContent = v.badge || '';
        if (v.badge) badgeEl.removeAttribute('hidden');
        else badgeEl.setAttribute('hidden', '');
      }

      if (thumbEl && v.image) thumbEl.src = v.image;

      if (paymentEl && v.pay) {
        var tpl = paymentEl.getAttribute('data-template') || '';
        if (tpl) paymentEl.textContent = tpl.split('[price]').join(v.pay);
      }

      if (button) setSoldOut(button, v.available === false);
    }

    /* One place that owns the bar's sold-out state, so the disabled flag and the wording can never
       disagree. Falls back to leaving the label alone when the theme did not supply one. */
    function setSoldOut(el, soldOut) {
      el.disabled = soldOut;
      el.setAttribute('aria-disabled', soldOut ? 'true' : 'false');
      var label = soldOut
        ? el.getAttribute('data-atc-label-sold-out')
        : el.getAttribute('data-atc-label');
      if (label) el.textContent = label;
    }

    /* ---- Delegate to whichever add-to-cart the page already has ---- */

    var themeForm = findThemeForm();
    var themeFormId = formIdOf(themeForm);
    var anchor = null;

    if (button && themeFormId) {
      button.setAttribute('type', 'submit');
      button.setAttribute('form', themeFormId);

      /* Observe the FORM, not <buy-buttons> — ProductRerender replaces the latter on
         every variant change (theme.js:3364), which would silently kill the observer.
         Impact's own ProductQuickAdd observes the form for the same reason. */
      anchor = themeForm;

      document.addEventListener('variant:change', function (event) {
        if (event.detail && event.detail.variant) applyVariant(event.detail.variant.id);
      });
    } else if (button && document.querySelector(ET_CTA)) {
      button.setAttribute('type', 'button');

      /* Re-query on click so the binding survives a section reload in the editor. */
      button.addEventListener('click', function () {
        var cta = document.querySelector(ET_CTA);
        if (cta && !cta.disabled) cta.click();
      });

      var etCta = document.querySelector(ET_CTA);
      anchor = etCta.closest('.et-pdp__cta-wrap') || etCta;
      applyVariant(etCta.getAttribute('data-variant-id'));

      new MutationObserver(function () {
        var cta = document.querySelector(ET_CTA);
        if (!cta) return;
        applyVariant(cta.getAttribute('data-variant-id'));
        /* The ET button is the source of truth on a PDP that has one - mirror its state, wording
           included, rather than only its disabled flag. */
        if (button) setSoldOut(button, cta.disabled);
      }).observe(etCta, {
        attributes: true,
        attributeFilter: ['data-variant-id', 'disabled']
      });
    }

    /* No add-to-cart to delegate to — leave the bar hidden rather than showing a
       button that cannot do anything. */
    if (!anchor) return;

    /* ---- Visibility ---- */

    if (root.getAttribute('data-scroll-trigger') !== 'true') {
      root.classList.add('is-visible');
      return;
    }

    /* .footer is stock Impact's class; every ET page renders ET - Footer instead, so the old
       selector matched nothing, footerReached stayed false and the bar covered the footer for good. */
    var footer = document.querySelector('.footer, .shopify-section--footer, .et-footer');
    var scrolledPast = false;
    var footerReached = false;

    function render() {
      root.classList.toggle('is-visible', scrolledPast && !footerReached);
    }

    /* The footer check never takes an offset, so it gets its own observer. */
    if (footer) {
      new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          footerReached = entry.isIntersecting;
        });
        render();
      }).observe(footer);
    }

    /* Mobile reveals the bar earlier: the trigger line sits `offset` px BELOW the top of the
       viewport instead of on it, so the bar arrives after that much less scrolling. rootMargin
       moves the observer's own boundary to the same line, otherwise the callback would not fire
       there. Desktop keeps offset 0 — the bar appears once the main ATC clears the top. */
    var mobileOffset = parseInt(root.getAttribute('data-mobile-trigger-offset'), 10) || 0;
    var mq = window.matchMedia('(max-width: 749px)');
    var anchorObserver = null;

    function watchAnchor() {
      if (anchorObserver) anchorObserver.disconnect();

      /* Never let the margin collapse the root to zero/negative height on a short viewport. */
      var offset = mq.matches ? Math.max(0, Math.min(mobileOffset, window.innerHeight - 100)) : 0;

      anchorObserver = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            scrolledPast = entry.boundingClientRect.bottom < offset;
          });
          render();
        },
        { rootMargin: '-' + offset + 'px 0px 0px 0px' }
      );
      anchorObserver.observe(anchor);
    }

    watchAnchor();
    if (mq.addEventListener) mq.addEventListener('change', watchAnchor);
    else if (mq.addListener) mq.addListener(watchAnchor);

    /* An IntersectionObserver only fires when the intersection STATE CHANGES, and on mobile the
       state never does. Measured on Enkel 2.0 at 390x844: the anchor's bottom is already 1032 at
       load, i.e. BELOW the root box that `-250px` rootMargin shrinks to 594 — so it starts
       not-intersecting, and scrolling carries it from "below the root" to "above the root"
       without ever touching it. false -> false, one callback for the whole page, and the bar
       stayed hidden at EVERY scroll position. Desktop only worked by luck: offset 0 leaves the
       anchor intersecting at load, so there is a true -> false transition to fire on.

       A rAF-throttled scroll read is the source of truth. The observer is kept because it still
       catches layout shifts that move the anchor without a scroll (variant change, image load). */
    var ticking = false;

    function measure() {
      ticking = false;
      var offset = mq.matches ? Math.max(0, Math.min(mobileOffset, window.innerHeight - 100)) : 0;
      scrolledPast = anchor.getBoundingClientRect().bottom < offset;
      render();
    }

    function onScroll() {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(measure);
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);
    measure();
  }

  function boot(scope) {
    var context = scope && scope.querySelectorAll ? scope : document;
    Array.prototype.forEach.call(
      context.querySelectorAll('[data-et-sticky-atc]'),
      initBar
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      boot(document);
    });
  } else {
    boot(document);
  }

  /* A section reload in the theme editor can replace the PDP the bar delegates to,
     so re-initialise every bar rather than only the reloaded subtree. */
  document.addEventListener('shopify:section:load', function () {
    Array.prototype.forEach.call(
      document.querySelectorAll('[data-et-sticky-atc]'),
      function (el) {
        delete el.dataset.etStickyInit;
      }
    );
    boot(document);
  });
})();
